// src/services/api/servicesAPI.js
import { createAuthHeaders } from '../../utils/authUtils';

const FIREBASE_FUNCTIONS_BASE_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_BASE_URL;

/**
 * Client-side API service for service operations
 * Calls Firebase Functions instead of direct database access
 */
class ServicesAPI {
  /**
   * Get all services
   * @param {Object} options - Query options
   * @param {boolean} options.activeOnly - Only return active services
   * @returns {Promise<Array>} Array of services
   */
  static async getAll(options = {}) {
    try {
      console.log('API: Getting all services', { options });
      
      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/services`);
        url.searchParams.set('operation', 'getAll');
        
        if (options.activeOnly !== undefined) {
          url.searchParams.set('activeOnly', options.activeOnly.toString());
        }
        
        console.log('API: Making request to:', url.toString());
        
        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });
        
        if (!response.ok) {
          await this._handleAPIError(response, 'getAll');
        }
        
        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }
        
        console.log('API: Successfully retrieved all services', {
          count: result.data?.length || 0,
          activeOnly: options.activeOnly
        });
        
        return result.data || [];
      });
      
    } catch (error) {
      console.error('API Error in getAll services:', {
        options,
        error: error.message,
        stack: error.stack
      });
      
      throw new Error(`Failed to get services: ${error.message}`);
    }
  }

  /**
   * Get service by ID
   * @param {string} serviceId - Service ID
   * @returns {Promise<Object|null>} Service data or null if not found
   */
  static async getById(serviceId) {
    try {
      console.log('API: Getting service by ID', { serviceId });
      
      // Validate parameters
      if (!serviceId) {
        throw new Error('Service ID is required');
      }

      // Validate UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(serviceId)) {
        throw new Error('Invalid service ID format');
      }

      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/services`);
        url.searchParams.set('operation', 'getById');
        url.searchParams.set('serviceId', serviceId);
        
        console.log('API: Making request to:', url.toString());
        
        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });
        
        if (!response.ok) {
          await this._handleAPIError(response, 'getById');
        }
        
        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }
        
        // Handle null result (service not found)
        if (result.data === null) {
          console.log('API: Service not found', { serviceId });
          return null;
        }
        
        console.log('API: Successfully retrieved service by ID', {
          serviceId: result.data.id,
          name: result.data.name,
          category: result.data.category
        });
        
        return result.data;
      });
      
    } catch (error) {
      console.error('API Error in getById service:', {
        serviceId,
        error: error.message,
        stack: error.stack
      });
      
      throw new Error(`Failed to get service by ID: ${error.message}`);
    }
  }

  static async getCustomerVisibleServices(options = {}) {
    try {
      console.log('API: Getting customer-visible services', { options });
      
      // Get all active services and filter on frontend for now
      // Later we can add a backend endpoint for this specific filter
      const allServices = await this.getAll({ activeOnly: true });
      
      // Filter to only customer-visible services
      const customerVisibleServices = allServices.filter(service => 
        service.is_visible_to_customers === true
      );
      
      console.log('API: Filtered customer-visible services', {
        totalServices: allServices.length,
        customerVisible: customerVisibleServices.length
      });
      
      return customerVisibleServices;
    } catch (error) {
      console.error('API Error in getCustomerVisibleServices:', {
        options,
        error: error.message
      });
      throw new Error(`Failed to get customer-visible services: ${error.message}`);
    }
  }

  static async updateVisibility(serviceId, visibilityData) {
    try {
      console.log('API: Updating service visibility', { serviceId, visibilityData });

      // Validate parameters
      if (!serviceId) {
        throw new Error('Service ID is required');
      }

      if (!visibilityData || typeof visibilityData !== 'object') {
        throw new Error('Visibility data is required');
      }

      // Prepare update data with only visibility fields
      const updateData = {};
      if (visibilityData.is_visible_to_customers !== undefined) {
        updateData.is_visible_to_customers = Boolean(visibilityData.is_visible_to_customers);
      }
      if (visibilityData.show_price_to_customers !== undefined) {
        updateData.show_price_to_customers = Boolean(visibilityData.show_price_to_customers);
      }

      if (Object.keys(updateData).length === 0) {
        throw new Error('No visibility fields to update');
      }

      // Use existing update method
      return await this.update(serviceId, updateData);
    } catch (error) {
      console.error('API Error in updateVisibility:', {
        serviceId,
        visibilityData,
        error: error.message
      });
      throw new Error(`Failed to update service visibility: ${error.message}`);
    }
  }

  /**
   * Create new service
   * @param {Object} serviceData - Service data
   * @returns {Promise<Object>} Created service
   */
  static async create(serviceData) {
    try {
      console.log('API: Creating new service', { 
        name: serviceData?.name,
        category: serviceData?.category,
        duration: serviceData?.duration,
        price: serviceData?.price,
        is_visible_to_customers: serviceData?.is_visible_to_customers,
        show_price_to_customers: serviceData?.show_price_to_customers
      });

      // Validate parameters
      if (!serviceData) {
        throw new Error('Service data is required');
      }

      if (typeof serviceData !== 'object' || Array.isArray(serviceData)) {
        throw new Error('Service data must be an object');
      }

      // Validate required fields
      if (!serviceData.name || !serviceData.name.trim()) {
        throw new Error('Service name is required');
      }

      // Validate visibility flags if provided
      if (serviceData.is_visible_to_customers !== undefined && typeof serviceData.is_visible_to_customers !== 'boolean') {
        throw new Error('is_visible_to_customers must be a boolean');
      }
      if (serviceData.show_price_to_customers !== undefined && typeof serviceData.show_price_to_customers !== 'boolean') {
        throw new Error('show_price_to_customers must be a boolean');
      }

      // Use retry mechanism for this request
      return await this._retryRequest(async () => {
        const url = `${FIREBASE_FUNCTIONS_BASE_URL}/services`;
        
        console.log('API: Making POST request to:', url);
        
        // Make the API call
        const response = await fetch(url, {
          method: 'POST',
          headers: await createAuthHeaders(),
          body: JSON.stringify({
            operation: 'create',
            serviceData: serviceData
          })
        });
        
        // Check if response is ok
        if (!response.ok) {
          await this._handleAPIError(response, 'create');
        }
        
        // Parse response
        const result = await response.json();
        
        // Check for API-level errors
        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }
        
        console.log('API: Successfully created service', {
          serviceId: result.data?.id,
          name: result.data?.name,
          category: result.data?.category,
          duration: result.data?.duration,
          price: result.data?.price
        });
        
        return result.data;
      });
      
    } catch (error) {
      console.error('API Error in create service:', {
        serviceData,
        error: error.message,
        stack: error.stack
      });
      
      // Re-throw with more context
      throw new Error(`Failed to create service: ${error.message}`);
    }
  }

  /**
   * Update existing service
   * @param {string} serviceId - Service ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Updated service
   */
  static async update(serviceId, updateData) {
    try {
      console.log('API: Updating service', { 
        serviceId,
        updateFields: Object.keys(updateData || {}),
        hasVisibilityUpdates: 'is_visible_to_customers' in updateData || 'show_price_to_customers' in updateData
      });

      // Validate parameters
      if (!serviceId) {
        throw new Error('Service ID is required');
      }

      if (!updateData || Object.keys(updateData).length === 0) {
        throw new Error('Update data is required');
      }

      // Validate UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(serviceId)) {
        throw new Error('Invalid service ID format');
      }

      // Validate visibility flags if provided
      if (updateData.is_visible_to_customers !== undefined && typeof updateData.is_visible_to_customers !== 'boolean') {
        throw new Error('is_visible_to_customers must be a boolean');
      }
      if (updateData.show_price_to_customers !== undefined && typeof updateData.show_price_to_customers !== 'boolean') {
        throw new Error('show_price_to_customers must be a boolean');
      }

      // Use retry mechanism for this request
      return await this._retryRequest(async () => {
        const url = `${FIREBASE_FUNCTIONS_BASE_URL}/services`;
        
        console.log('API: Making POST request to:', url);
        
        // Make the API call
        const response = await fetch(url, {
          method: 'POST',
          headers: await createAuthHeaders(),
          body: JSON.stringify({
            operation: 'update',
            serviceId: serviceId,
            updateData: updateData
          })
        });
        
        // Check if response is ok
        if (!response.ok) {
          await this._handleAPIError(response, 'update');
        }
        
        // Parse response
        const result = await response.json();
        
        // Check for API-level errors
        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }
        
        console.log('API: Successfully updated service', {
          serviceId: result.data?.id,
          name: result.data?.name,
          updatedFields: Object.keys(updateData)
        });
        
        return result.data;
      });
      
    } catch (error) {
      console.error('API Error in update service:', {
        serviceId,
        updateData,
        error: error.message,
        stack: error.stack
      });
      
      // Re-throw with more context
      throw new Error(`Failed to update service: ${error.message}`);
    }
  }

  /**
   * Update service status (active/inactive)
   * @param {string} serviceId - Service ID
   * @param {boolean} isActive - New active status
   * @returns {Promise<Object>} Updated service
   */
  static async updateStatus(serviceId, isActive) {
    try {
      console.log('API: Updating service status', { serviceId, isActive });

      // Validate parameters
      if (!serviceId) {
        throw new Error('Service ID is required');
      }

      if (typeof isActive !== 'boolean') {
        throw new Error('isActive must be a boolean value');
      }

      // Validate UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(serviceId)) {
        throw new Error('Invalid service ID format');
      }

      // Use retry mechanism for this request
      return await this._retryRequest(async () => {
        const url = `${FIREBASE_FUNCTIONS_BASE_URL}/services`;
        
        console.log('API: Making POST request to:', url);
        
        // Make the API call
        const response = await fetch(url, {
          method: 'POST',
          headers: await createAuthHeaders(),
          body: JSON.stringify({
            operation: 'updateStatus',
            serviceId: serviceId,
            isActive: isActive
          })
        });
        
        // Check if response is ok
        if (!response.ok) {
          await this._handleAPIError(response, 'updateStatus');
        }
        
        // Parse response
        const result = await response.json();
        
        // Check for API-level errors
        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }
        
        console.log('API: Successfully updated service status', {
          serviceId: result.data?.id,
          name: result.data?.name,
          isActive: result.data?.is_active
        });
        
        return result.data;
      });
      
    } catch (error) {
      console.error('API Error in updateStatus service:', {
        serviceId,
        isActive,
        error: error.message,
        stack: error.stack
      });
      
      // Re-throw with more context
      throw new Error(`Failed to update service status: ${error.message}`);
    }
  }

  /**
   * Delete service
   * @param {string} serviceId - Service ID
   * @returns {Promise<Object>} Deletion result
   */
  static async delete(serviceId) {
    try {
      console.log('API: Deleting service', { serviceId });

      // Validate parameters
      if (!serviceId) {
        throw new Error('Service ID is required');
      }

      // Validate UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(serviceId)) {
        throw new Error('Invalid service ID format');
      }

      // Use retry mechanism for this request
      return await this._retryRequest(async () => {
        const url = `${FIREBASE_FUNCTIONS_BASE_URL}/services`;
        
        console.log('API: Making POST request to:', url);
        
        // Make the API call
        const response = await fetch(url, {
          method: 'POST',
          headers: await createAuthHeaders(),
          body: JSON.stringify({
            operation: 'delete',
            serviceId: serviceId
          })
        });
        
        // Check if response is ok
        if (!response.ok) {
          await this._handleAPIError(response, 'delete');
        }
        
        // Parse response
        const result = await response.json();
        
        // Check for API-level errors
        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        // Validate response data
        if (!result.data || !result.data.deletedServiceId) {
          throw new Error('Invalid deletion response: missing deletion confirmation data');
        }

        // Verify the deleted service ID matches the request
        if (result.data.deletedServiceId !== serviceId) {
          throw new Error('Response service ID does not match deletion request ID');
        }
        
        console.log('API: Service deletion completed successfully', {
          deletedServiceId: result.data.deletedServiceId,
          serviceName: result.data.deletedService?.name,
          deletionTimestamp: result.data.deletionTimestamp
        });
        
        return result.data;
      });
      
    } catch (error) {
      console.error('API Error in delete service:', {
        serviceId,
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      });
      
      // Re-throw with more context
      throw new Error(`Failed to delete service: ${error.message}`);
    }
  }

  /**
   * Check if service name already exists
   * @param {string} name - Service name to check
   * @param {string} excludeId - Service ID to exclude from check (for updates)
   * @returns {Promise<boolean>} True if name exists
   */
  static async checkNameExists(name, excludeId = null) {
    try {
      console.log('API: Checking if service name exists', { name, excludeId });
      
      // Validate parameters
      if (!name || !name.trim()) {
        throw new Error('Service name is required');
      }

      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/services`);
        url.searchParams.set('operation', 'checkNameExists');
        url.searchParams.set('name', name.trim());
        
        if (excludeId) {
          // Validate UUID format for excludeId
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
          if (!uuidRegex.test(excludeId)) {
            throw new Error('Invalid excludeId format');
          }
          url.searchParams.set('excludeId', excludeId);
        }
        
        console.log('API: Making request to:', url.toString());
        
        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });
        
        if (!response.ok) {
          await this._handleAPIError(response, 'checkNameExists');
        }
        
        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }
        
        const exists = result.data?.exists || false;
        
        console.log('API: Successfully checked service name existence', {
          name,
          excludeId,
          exists
        });
        
        return exists;
      });
      
    } catch (error) {
      console.error('API Error in checkNameExists service:', {
        name,
        excludeId,
        error: error.message,
        stack: error.stack
      });
      
      throw new Error(`Failed to check service name existence: ${error.message}`);
    }
  }

  /**
   * Generic error handler for API responses
   * @private
   * @param {Response} response - Fetch response object
   * @param {string} operation - Operation name for error context
   */
  static async _handleAPIError(response, operation) {
    try {
      const data = await response.json();
      throw new Error(`${operation} failed: ${data.error || data.message || 'Unknown error'}`);
    } catch (jsonError) {
      // If we can't parse the JSON, use the HTTP status
      throw new Error(`${operation} failed: HTTP ${response.status} - ${response.statusText}`);
    }
  }
  
  /**
   * Generic retry mechanism for failed requests
   * @private
   * @param {Function} requestFn - Function that makes the actual request
   * @param {number} maxRetries - Maximum number of retry attempts
   * @param {number} delay - Initial delay between retries in milliseconds
   * @returns {Promise<*>} Result of the request function
   */
  static async _retryRequest(requestFn, maxRetries = 2, delay = 1000) {
    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error;
        
        // Don't retry on validation errors (4xx)
        if (error.message.includes('400') || error.message.includes('404') || error.message.includes('401')) {
          console.log('API: Not retrying due to client error:', error.message);
          throw error;
        }
        
        // If this is the last attempt, throw the error
        if (attempt === maxRetries) {
          console.log('API: Max retries reached, throwing error');
          throw error;
        }
        
        // Wait before retrying
        console.log(`API: Retrying request in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 2; // Exponential backoff
      }
    }
    
    throw lastError;
  }
  
  /**
   * Test API connectivity
   * @returns {Promise<boolean>} True if API is accessible
   */
  static async testConnection() {
    try {
      console.log('API: Testing connection...');
      await this.getAll({ activeOnly: true });
      console.log('API: Connection test successful');
      return true;
    } catch (error) {
      console.error('API: Connection test failed:', error.message);
      return false;
    }
  }
  
  /**
   * Get the base URL for Firebase Functions
   * @returns {string} Base URL
   */
  static getBaseURL() {
    return FIREBASE_FUNCTIONS_BASE_URL;
  }
  
  /**
   * Check if the API is configured correctly
   * @returns {boolean} True if configuration looks valid
   */
  static isConfigured() {
    return !!FIREBASE_FUNCTIONS_BASE_URL && FIREBASE_FUNCTIONS_BASE_URL.includes('cloudfunctions.net');
  }
}

export default ServicesAPI;